import {
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import { EContractStatus, EDashboardSortFields } from "api/enums";
import ContractModel from "api/models/ContractModel";
import { FilterIcon } from "assets/icons/CustomSVGIcons";
import { paletteColors } from "assets/styles/colors";
import clsx from "clsx";
import { ITableColumn } from "components/interfaces";
import DateRangePickerField from "components/UI/Fields/DateRangePickerField";
import SearchField from "components/UI/Fields/SearchField";
import { HeadTable, Row } from "components/UI/Table";
import { lang } from "i18nLang";
import { observer } from "mobx-react";
import { useState } from "react";
import { DashboardStore, UserStore } from "store";

const useStyles = makeStyles({
  label: {
    fontWeight: 500,
    fontSize: "0.75rem",
    textTransform: "uppercase",
  },
  labelDate: {
    marginBottom: -10,
  },
  labelProject: {
    marginBottom: "1rem",
  },
  chipContainer: {
    marginTop: 5,
  },
  chip: {
    marginRight: 4,
  },
  mRight: {
    marginRight: "1rem",
  },
  textOverflow: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "block",
    width: 180,
  },
  tableContainer: {
    maxHeight: 260,
    overflowY: "auto",
  },
  tableHeadCell: { fontSize: "0.75rem" },
  textCenter: {
    textAlign: "center",
  },
  emptyLabel: {
    fontWeight: 700,
  },
});

export const CellTooltip = ({ data }) => {
  const classes = useStyles();
  return (
    <Tooltip title={data}>
      <Typography variant="inherit" className={classes.textOverflow}>
        {data}
      </Typography>
    </Tooltip>
  );
};

const ContractFilters = observer(() => {
  const classes = useStyles();
  const statusList = Object.values(EContractStatus);
  const { filters, filteredContracts, selectedContractsUids, inputSearch } =
    DashboardStore;
  const [open, setOpen] = useState(false);
  const columns: ITableColumn[] = [
    { dataKey: "select", label: lang("dashboard.select"), width: 100 },
    {
      dataKey: "project",
      label: lang("dashboard.fields.project"),
      width: 200,
      sorting: true,
    },
    {
      dataKey: "contract",
      label: lang("dashboard.fields.contract"),
      width: 200,
      sorting: true,
    },
    {
      dataKey: "client",
      label: lang("dashboard.fields.client"),
      width: 200,
      sorting: true,
    },
    {
      dataKey: "location",
      label: lang("dashboard.fields.location"),
      width: 200,
      sorting: true,
    },
    {
      dataKey: "createdAt",
      label: lang("dashboard.fields.createdAt"),
      width: 150,
      sorting: true,
    },
  ];

  const openModal = () => setOpen(true);
  const closeModal = () => setOpen(false);

  const handleClickCancel = () => {
    DashboardStore.setFiltersWithUserData();
    DashboardStore.filterAndSetFilteredContracts();
    closeModal();
  };

  const handleClickClear = () => {
    DashboardStore.resetFilters();
    DashboardStore.filterAndSetFilteredContracts();
  };

  const handleClickFilter = () => {
    const filters = {
      ...DashboardStore.filters,
      selectedStatus: [...DashboardStore.filters.selectedStatus],
    };
    UserStore.saveDashboardFilters(filters);
    DashboardStore.refreshContractsData();
    closeModal();
  };

  const handleChangeDateRange = (rangeDate: {
    start: Date | null;
    end: Date | null;
  }) => {
    DashboardStore.setFilterPeriodDateRange(rangeDate);
    DashboardStore.filterAndSetFilteredContracts();
    DashboardStore.filterSelectedContracts();
  };

  const handleClickChip = (status: string) => {
    DashboardStore.setFilterStatus(status);
    DashboardStore.filterAndSetFilteredContracts();
    DashboardStore.filterSelectedContracts();
  };

  const handleCheckRow = (contract: ContractModel, checked: boolean) => {
    if (checked) {
      DashboardStore.addSelectedContractUid(contract.uid);
    } else {
      DashboardStore.removeSelectedContractUid(contract.uid);
    }
    DashboardStore.filterAndSetFilteredContracts();
  };

  const handleChangeInputSearch = (ev) => {
    const { value } = ev.target;
    DashboardStore.setInputSearch(value);
    DashboardStore.filterAndSetFilteredContracts();
    DashboardStore.filterSelectedContracts();
  };

  const handleSortColumn = (ev, field: string) => {
    DashboardStore.setSortField(field as EDashboardSortFields);
    DashboardStore.filterAndSetFilteredContracts();
  };

  function getContractsTableRows() {
    return filteredContracts.map((contract) => {
      const createdAt = contract.createdAt.toLocaleDateString();
      const selected = selectedContractsUids.includes(contract.uid);
      return {
        id: contract.uid,
        select: (
          <Checkbox
            checked={selected}
            color="primary"
            onChange={(ev, checked) => handleCheckRow(contract, checked)}
          />
        ),
        project: <CellTooltip data={contract.development.name} />,
        contract: <CellTooltip data={contract.name} />,
        client: <CellTooltip data={contract.development.customer} />,
        location: <CellTooltip data={contract.development.local} />,
        createdAt,
      };
    });
  }

  return (
    <>
      <Button
        color="primary"
        startIcon={<FilterIcon fill={paletteColors.primary.main} />}
        onClick={openModal}
      >
        {lang("filterNoun")}
      </Button>
      <Dialog
        open={open}
        aria-labelledby="dlgContractFilter"
        fullWidth={true}
        maxWidth="lg"
        onClose={closeModal}
      >
        <DialogTitle id="dlgContractFilter">{lang("filter")}</DialogTitle>
        <DialogContent>
          <Grid container>
            <FormControl className={classes.mRight} margin="normal">
              <Typography
                className={clsx(classes.label, classes.labelDate)}
                variant="body1"
              >
                {lang("dashboard.byDate")}
              </Typography>
              <DateRangePickerField
                initialDate={filters.periodInitialDate || null}
                finalDate={filters.periodFinalDate || null}
                emptyLabel={lang("dashboard.filterByDate")}
                onChange={handleChangeDateRange}
              />
            </FormControl>
            <FormControl margin="normal">
              <Typography className={classes.label} variant="body1">
                {lang("dashboard.byStatus")}
              </Typography>
              <Grid className={classes.chipContainer}>
                {statusList.map((stt) => (
                  <Chip
                    key={stt}
                    className={classes.chip}
                    icon={
                      filters.selectedStatus.includes(stt) ? (
                        <CheckIcon />
                      ) : undefined
                    }
                    label={lang(`kanban.status.${stt}`)}
                    color={
                      filters.selectedStatus.includes(stt)
                        ? "primary"
                        : "default"
                    }
                    onClick={() => handleClickChip(stt)}
                  />
                ))}
              </Grid>
            </FormControl>
          </Grid>
          <Grid container>
            <FormControl margin="normal" fullWidth>
              <Typography
                className={clsx(classes.label, classes.labelProject)}
                variant="body1"
              >
                {lang("dashboard.byProject")}
              </Typography>
              <SearchField
                label={lang("search")}
                fullWidth
                value={inputSearch}
                onChange={handleChangeInputSearch}
              />
            </FormControl>
          </Grid>
          <Grid container className={classes.tableContainer}>
            <Table stickyHeader size="small">
              <HeadTable
                classNameCell={classes.tableHeadCell}
                columns={columns}
                fontWeight={500}
                onClickColumn={handleSortColumn}
              />
              <TableBody>
                {getContractsTableRows().map((contract) => (
                  <Row key={contract.id} columns={columns} row={contract} />
                ))}
                {!filteredContracts.length && (
                  <TableRow>
                    <TableCell
                      className={classes.textCenter}
                      colSpan={columns.length}
                    >
                      <Typography
                        className={classes.emptyLabel}
                        variant="body1"
                      >
                        {lang("dashboard.emptyTableTitle")}
                      </Typography>
                      <Typography variant="caption">
                        {lang("dashboard.emptyTableSubtitle")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button color="default" onClick={handleClickCancel}>
            {lang("cancel")}
          </Button>
          <Button color="primary" onClick={handleClickClear}>
            {lang("dashboard.clearFilters")}
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleClickFilter}
          >
            {lang("filter")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default ContractFilters;
